import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { invoiceSubmit, setStage, setSubStage } from '../../../../slices/eligibility/eligibilitySlice';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Loader from '../../../common/Loader';
import ImageComponent from '../../../common/Image';
import { toast } from 'react-toastify';
import Modal, { ModalBody, ModalHeader } from '../../../common/Modal';
import DatePicker from './datePicker';

function InvoiceDetails() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const referenceId = searchParams.get('referenceId');
    const { userData } = useSelector((state) => state.auth);
    const { customerName } = useSelector(state => state.eligibility)
    // Handler to delete uploaded images
    const handleDeleteFile = (fieldName) => {
        formik.setFieldValue(fieldName, null);
        formik.setFieldError(fieldName, '');
    }
    const userName = userData?.response_data?.data?.userName || '';
    const { imeiNumber, productCost, applicationDetail } = useSelector(state => state?.eligibility)
    const [loader, setLoader] = useState()
    const [showImeiConfirmModal, setShowImeiConfirmModal] = useState(false);
    const [showValidationSuccessModal, setShowValidationSuccessModal] = useState(false);

    console.log(imeiNumber, productCost)

    const formik = useFormik({
        initialValues: {
            invoice_date: applicationDetail?.invoice_date ?? '',
            invoice_number: applicationDetail?.invoice_number ?? '',
            invoice_amount: applicationDetail?.invoice_amount ?? productCost,
            imei_number: imeiNumber,
            invoice_base_64: applicationDetail?.invoice_base_64 ? `data:image/png;base64,${applicationDetail?.invoice_base_64}` : null,
            additional_document_base_64: applicationDetail?.additional_document_base_64 ? `data:image/png;base64,${applicationDetail?.additional_document_base_64}` : null,
            invoice_file_type: "image"
        },
        validationSchema: Yup.object({
            invoice_date: Yup.string().required('Invoice Date is required'),
            invoice_number: Yup.string().required('Invoice Number is required'),
            invoice_amount: Yup.number()
                .typeError('Amount must be a number')
                .required('Invoice Amount is required')
                .test(
                    'min-invoice-amount',
                    `Invoice Amount must be greater than or equal to ₹${Number(productCost).toLocaleString('en-IN')}`,
                    function (value) {
                        return value >= productCost;
                    }
                ),
            imei_number: Yup.string().required('IMEI Number is required'),
            invoice_base_64: Yup.mixed().required('Invoice image is required'),
        }),
        onSubmit: async (values) => {
            // Open confirm modal instead of directly calling API
            handleConfirmSubmit()
        }
    });

    const handleConfirmSubmit = async () => {
        // Close confirm modal
        setShowImeiConfirmModal(false);

        // Prepare data and call API
        let data = {
            reference_id: referenceId,
            ...formik.values
        }

        setLoader(true);
        const response = await dispatch(invoiceSubmit(data)).unwrap();
        setLoader(false);

        if (response?.response_code === 'S100') {
            // On success, open success modal
            setShowValidationSuccessModal(true);
        } else {
            !response?.payload?.error &&
                toast.error(response?.response_string || "Something went wrong", {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined
                });
        }
    };

    const onlyNumberField = (event) => {
        formik.setFieldValue(event?.target?.name, event?.target?.value.replace(/[^0-9]/g, ''));
    }


    const compressImageToBase64 = async (file, maxSizeInBytes) => {
        const bitmap = await createImageBitmap(file);

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const MAX_WIDTH = 1200; // adjust as needed
        let scale = 1;
        if (bitmap.width > MAX_WIDTH) {
            scale = MAX_WIDTH / bitmap.width;
        }

        canvas.width = bitmap.width * scale;
        canvas.height = bitmap.height * scale;
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

        let quality = 0.9;
        let base64;

        do {
            base64 = canvas.toDataURL("image/jpeg", quality); // Base64 string
            const sizeInBytes = Math.round((base64.length * 3) / 4); // estimate
            if (sizeInBytes <= maxSizeInBytes || quality <= 0.1) break;
            quality -= 0.1;
        } while (true);

        return base64;
    };

    const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result); // already Base64
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(file);
        });

    const handleFileChange = async (event) => {
        const fieldName = event.currentTarget.name;
        const file = event.currentTarget.files[0];
        if (!file) return;

        const imageTypes = ["image/jpeg", "image/jpg", "image/png"];
        const pdfTypes = ["application/pdf"];

        if (![...imageTypes, ...pdfTypes].includes(file.type)) {
            formik.setFieldValue(fieldName, "");
            formik.setFieldError(fieldName, "Only JPEG, JPG, PNG, or PDF files are allowed");
            return;
        }

        try {
            let base64;

            if (imageTypes.includes(file.type)) {
                // Compress images
                base64 = await compressImageToBase64(file, 6 * 1024 * 1024);
            } else if (pdfTypes.includes(file.type)) {
                // Read PDF as DataURL directly (don’t use canvas!)
                base64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result); // already includes data:application/pdf;base64
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
            }
            formik?.setFieldValue("invoice_file_type", imageTypes.includes(file.type) ? "image" : "pdf")
            formik.setFieldValue(fieldName, base64);
            formik.setFieldError(fieldName, "");
        } catch (err) {
            console.error("File processing error:", err);
            formik.setFieldValue(fieldName, "");
            formik.setFieldError(fieldName, "File processing failed");
        }
    };

    const handleHome = (e) => {
        e.preventDefault();
        navigate('/dashboard');
    };

    const formatDate = (dateInput) => {
        const date = new Date(dateInput);

        if (isNaN(date)) return ''; // safety check
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const todayDate = applicationDetail?.imei_validation_success_date ? applicationDetail?.imei_validation_success_date : today

    const today = new Date(todayDate);
    const threeDaysAfter = new Date(todayDate);
    threeDaysAfter.setDate(today.getDate() + 3);

    const ensurePdfBase64 = (base64) => {
        if (!base64.startsWith("data:application/pdf")) {
            return `data:application/pdf;base64,${base64}`;
        }
        return base64;
    };


    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                {loader === true && <Loader />}
                <div className="col-md-9 col-sm-12 Side-bank-Right">
                    <div className="row static-tab">
                        {/* Invoice Date */}
                        <div className="col-12 floating-bottom">
                            <div className="input-container has-icon">
                                <div>
                                    <DatePicker
                                        value={formik.values.invoice_date}
                                        onChange={(date) => {
                                            formik.setFieldValue('invoice_date', date);
                                        }}
                                        label="Invoice Date"
                                        required={true}
                                        minDate={formatDate(applicationDetail?.imei_validation_success_date ? applicationDetail?.imei_validation_success_date : today)}
                                        maxDate={formatDate(threeDaysAfter)}
                                        placeholder=' '
                                        autoComplete="off"
                                    />
                                    <label htmlFor="invoice_date" className="label form-label dob-label">Invoice Date</label>
                                    <div className="underline"></div>
                                </div>
                                {formik.touched.invoice_date && formik.errors.invoice_date && (
                                    <div className="text-danger small">{formik.errors.invoice_date}</div>
                                )}
                            </div>
                        </div>

                        {/* Invoice Number */}
                        <div className="col-12 floating-bottom">
                            <div className="input-container has-icon">
                                <input
                                    className="form-control input-underline"
                                    type="text"
                                    name="invoice_number"
                                    id="invoice_number"
                                    value={formik.values.invoice_number}
                                    onChange={(e) => {
                                        let val = e.target.value;

                                        // Prevent leading spaces
                                        if (val.startsWith(' ')) {
                                            val = val.trimStart();
                                        }

                                        formik.setFieldValue('invoice_number', val);
                                    }}
                                    onBlur={formik.handleBlur}
                                    placeholder=" "
                                    required
                                />
                                <label htmlFor="invoice_number" className="label form-label">Invoice Number</label>
                                <div className="underline"></div>
                                {formik.touched.invoice_number && formik.errors.invoice_number && (
                                    <div className="text-danger small">{formik.errors.invoice_number}</div>
                                )}
                            </div>
                        </div>

                        {/* Invoice Amount */}
                        <div className="col-12 floating-bottom">
                            <div className="input-container has-icon">
                                <input
                                    type="tel"
                                    className="form-control input-underline"
                                    id="invoice_amount"
                                    name="invoice_amount"
                                    value={formik.values.invoice_amount}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    onKeyUp={onlyNumberField}
                                    placeholder=' '
                                    required
                                    readOnly
                                />
                                <label htmlFor="invoice_amount" className="label form-label">Invoice Amount</label>
                                <div className="underline"></div>
                                {formik.touched.invoice_amount && formik.errors.invoice_amount && (
                                    <div className="text-danger small">{formik.errors.invoice_amount}</div>
                                )}
                            </div>
                        </div>

                        {/* IMEI Number */}
                        <div className="col-12 floating-bottom">
                            <div className="input-container has-icon">
                                <input
                                    type="text"
                                    className="form-control input-underline"
                                    id="imei_number"
                                    name="imei_number"
                                    value={formik.values.imei_number || "-"}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    readOnly
                                />
                                <label htmlFor="imei_number" className="label form-label">Invoice Serial/ IMEI Number</label>
                                <div className="underline"></div>
                            </div>
                            {formik.touched.imei_number && formik.errors.imei_number && (
                                <div className="text-danger small">{formik.errors.imei_number}</div>
                            )}
                        </div>
                        <div className="col-12 floating-bottom">
                            <p className="small fw-semibold mb-1">Invoice Image</p>
                            {!formik?.values?.invoice_base_64 && <div className="d-flex align-items-center gap-4">
                                <div className="btn f-upload">
                                    <input
                                        type="file"
                                        name="invoice_base_64"
                                        id="file-1"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="inputfile inputfile-1"
                                        capture="camera"
                                    />
                                    <label htmlFor="file-1">
                                        <i className="icon-mediacamera"></i>
                                        <div>
                                            Scan of Invoice
                                        </div>
                                    </label>
                                </div>
                                <div className="btn scanBtn">
                                    <input
                                        type="file"
                                        name="invoice_base_64"
                                        id="file-3"
                                        accept=".jpeg, .jpg, .png, .pdf"
                                        onChange={handleFileChange}
                                        capture={false}
                                        className="inputfile inputfile-1"
                                    />
                                    <label htmlFor="file-3">
                                        <i className="icon-arrowexport-E1"></i>

                                        <div>
                                            <span>Upload
                                                Invoice </span>
                                        </div>
                                    </label>
                                </div>
                            </div>}
                            {formik?.values?.invoice_base_64 &&
                                <div className="d-flex align-items-center gap-2">
                                    {formik.values.invoice_base_64?.startsWith("data:application/pdf") ? (
                                        <div
                                        >
                                            <ImageComponent
                                                fileName="PDF_file_icon.svg" // 👈 replace with your own PDF thumbnail icon
                                                alt="PDF file"
                                                style={{ width: "70px", height: "70px" }}
                                            />
                                        </div>
                                    ) : (
                                        <img loading="lazy"
                                            src={formik.values.invoice_base_64}
                                            alt="Invoice preview"
                                            style={{ width: "100px", height: "auto", borderRadius: "4px", border: "2px solid #000" }}
                                        />
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 ms-2"
                                        onClick={() => {
                                            const base64Data = formik.values.invoice_base_64;
                                            if (!base64Data) return;

                                            // Extract base64 body
                                            const byteString = atob(base64Data.split(",")[1]);
                                            const mimeString = base64Data.split(",")[0].split(":")[1].split(";")[0];

                                            const ab = new ArrayBuffer(byteString.length);
                                            const ia = new Uint8Array(ab);
                                            for (let i = 0; i < byteString.length; i++) {
                                                ia[i] = byteString.charCodeAt(i);
                                            }

                                            const blob = new Blob([ab], { type: mimeString });
                                            const url = URL.createObjectURL(blob);

                                            window.open(url, "_blank"); // open in new tab
                                        }}
                                        aria-label="share Invoice Image"
                                    >
                                        <ImageComponent fileName="eye.svg" alt="eye" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 ms-2"
                                        onClick={() => handleDeleteFile('invoice_base_64')}
                                        aria-label="Delete Invoice Image"
                                    >
                                        <ImageComponent fileName="delete.svg" alt="delete" />
                                    </button>
                                </div>
                            }
                            {formik.touched.invoice_base_64 && formik.errors.invoice_base_64 && (
                                <div className="text-danger small">{formik.errors.invoice_base_64}</div>
                            )}
                            <p className="info-txt text-secondary d-flex align-items-center gap-1 mb-0">
                                <i className="icon-EssetionalInformation-1"></i>
                                Note: Support the format JPEG, JPG, PNG &amp; PDF less than 2MB
                            </p>
                        </div>
                        <div className="col-12 floating-bottom">
                            <p className="small fw-semibold mb-1">Upload Additional Documents / Approval mail</p>
                            {!formik?.values?.additional_document_base_64 &&
                                <div className="align-items-center gap-4 ">
                                    {/* <div className="btn f-upload">
                                    <input
                                        type="file"
                                        name="product_base_64"
                                        id="file-2"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="inputfile inputfile-1"
                                        capture="camera"
                                    />
                                    <label htmlFor="file-2">
                                        <i className="icon-mediacamera"></i>

                                        <div>
                                            Scan of Product Openbox

                                        </div>
                                    </label>
                                </div> */}
                                    {/* <button className="btn scanBtn" data-bs-toggle="modal" data-bs-target="#">
                                <i className="icon-arrowexport-E1"></i>
                                <div>
                                    <span className="fw-semibold">Upload
                                        Invoice </span>
                                </div>
                            </button> */}
                                    <div className="btn scanBtn">
                                        <input
                                            type="file"
                                            name="additional_document_base_64"
                                            id="file-2"
                                            accept=".jpeg, .jpg, .png, .pdf"
                                            onChange={handleFileChange}
                                            capture={false}
                                            className="inputfile inputfile-1"
                                        />
                                        <label htmlFor="file-2">
                                            <i className="icon-arrowexport-E1"></i>

                                            <div>
                                                <span>Upload
                                                    Additional Documents / Approval mail</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>}
                            {formik?.values?.additional_document_base_64 &&
                                <div className="d-flex align-items-center gap-2">
                                    {formik.values.additional_document_base_64?.startsWith("data:application/pdf") ? (
                                        <div
                                        >
                                            <ImageComponent
                                                fileName="PDF_file_icon.svg" // 👈 replace with your own PDF thumbnail icon
                                                alt="PDF file"
                                                style={{ width: "70px", height: "70px" }}
                                            />
                                        </div>
                                    ) : (
                                        <img loading="lazy"
                                            src={formik.values.additional_document_base_64}
                                            alt="Invoice preview"
                                            style={{ width: "100px", height: "auto", borderRadius: "4px", border: "2px solid #000" }}
                                        />
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 ms-2"
                                        onClick={() => {
                                            const base64Data = formik.values.additional_document_base_64;
                                            if (!base64Data) return;

                                            // Extract base64 body
                                            const byteString = atob(base64Data.split(",")[1]);
                                            const mimeString = base64Data.split(",")[0].split(":")[1].split(";")[0];

                                            const ab = new ArrayBuffer(byteString.length);
                                            const ia = new Uint8Array(ab);
                                            for (let i = 0; i < byteString.length; i++) {
                                                ia[i] = byteString.charCodeAt(i);
                                            }

                                            const blob = new Blob([ab], { type: mimeString });
                                            const url = URL.createObjectURL(blob);

                                            window.open(url, "_blank"); // open in new tab
                                        }}
                                        aria-label="View Additional Image"
                                    >
                                        <ImageComponent fileName="eye.svg" alt="eye" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 ms-2"
                                        onClick={() => handleDeleteFile('additional_document_base_64')}
                                        aria-label="Delete Additional Document"
                                    >
                                        <ImageComponent fileName="delete.svg" alt="delete" />
                                    </button>
                                </div>
                            }
                            {formik.touched.additional_document_base_64 && formik.errors.additional_document_base_64 && (
                                <div className="text-danger small">{formik.errors.additional_document_base_64}</div>
                            )}
                            <p className="info-txt text-secondary d-flex align-items-center gap-1 mb-0">
                                <i className="icon-EssetionalInformation-1"></i>
                                Note: Support the format JPEG, JPG, PNG &amp; PDF less than 2MB
                            </p>
                        </div>
                    </div>
                </div>

                <footer className="sticky-bottom-1">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-sm-12">
                                <div className="d-flex justify-content-end">
                                    <button className="btn btn-primary" type="submit">
                                        Proceed
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>
            </form>

            <Modal
                id="imeiConfirmModal"
                show={showImeiConfirmModal}
                onClose={() => setShowImeiConfirmModal(false)}
                modalClass="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg bottom-modal custom-modal"
            >
                <ModalHeader className="">
                    <div className="top-draw"></div>
                </ModalHeader>
                <ModalBody className="mt-3 mb-3">
                    <div className="px-3 py-3 text-center">
                        <ImageComponent fileName="2fverify.svg" />
                        <p className="fw-semibold m-0 mt-3">Do you want to adjust against the trade advance ?</p>
                        <div className="d-flex gap-4 justify-content-center mt-4">
                            <button
                                className="btn btn-outline-primary flex-fill"
                                type="button"
                                onClick={handleConfirmSubmit}
                            >
                                No
                            </button>
                            <button
                                className="btn btn-primary btn-lg signin flex-fill"
                                type="button"
                                onClick={handleConfirmSubmit}
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </ModalBody>
            </Modal>

            <Modal
                id="successModal"
                show={showValidationSuccessModal}
                onClose={() => setShowValidationSuccessModal(false)}
                modalClass="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg bottom-modal custom-modal"
            >
                <ModalHeader className="">
                    <div className="top-draw"></div>
                </ModalHeader>
                <ModalBody className="mt-3 mb-3">
                    <div className="px-3 py-3 text-center">
                        <ImageComponent fileName="2fverify.svg" />
                        <p className="m-0 mt-3">Application ID : </p>
                        <p className="fw-semibold m-0 mt-3">{referenceId}</p>
                        <p className="m-0 mt-3"> for</p>
                        <p className="fw-semibold m-0 mt-3">{customerName}</p>
                        <p className="m-0 mt-3"> has been submitted for disbursement</p>
                        <button
                            className="btn btn-primary btn-lg signin flex-fill"
                            type="button"
                            onClick={handleHome}
                        >
                            Go to Home
                        </button>
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
}

export default InvoiceDetails;